<template>
  <div class="login">
    <h3>{{ $t('loginForm.title') }}</h3>
    <input type="text"
      class="idInput"
      ref="inputId"
      :placeholder="$t('loginForm.inputId.ph')"
      :aria-label="$t('loginForm.inputId.label')"
      v-model="userId"
      @keydown.enter="setFocus('inputPw')"
    >
    <input type="password"
      class="pwInput"
      ref="inputPw"
      :placeholder="$t('loginForm.inputPw.ph')"
      :aria-label="$t('loginForm.inputPw.label')"
      v-model="password"
      @keydown.enter="loginProcess"
      @keyup="checkCapsLock"
    >
    <div class="valid-message" v-if="isCapsLock">
      {{ $t('loginForm.message.onCapsLock') }}
    </div>
    <div class="findBox">
      <label>
        <input type="checkbox" v-model="useRemem"/>{{ $t('loginForm.chkRememId') }}
      </label>
      <div class="divBtnFlex">
        <button class="findId" @click="showModal('findIdModal')">{{ $t('loginForm.btnFindId') }}</button>
        <div></div>
        <button class="findPW" @click="showModal('findPwModal')">{{ $t('loginForm.btnFindPW') }}</button>
      </div>
    </div>
    <button class="btnLogin"
      @keydown.enter="loginProcess"
      @click="loginProcess"
    >
      {{ $t('loginForm.btnLogin') }}
    </button>
    <a class="btnDown" target="_blank" rel="noopener noreferrer" href="https://auth.hknu.ac.kr:21443/download/apps">
      <font-awesome-icon :icon="['fas', 'mobile-alt']" />
      {{ $t('loginForm.btnDown') }}
    </a>
    <modal ref="findIdModal" :closable="true">
      <template v-slot:header>
        {{ $t('loginForm.modal.findId.title') }}
      </template>
      <id-find-form @success="closeModal('findIdModal')" @failed="closeModal('findIdModal')"/>
      <template v-slot:footer>
      </template>
    </modal>
    <modal ref="findPwModal" :closable="true">
      <template v-slot:header>
        {{ $t('loginForm.modal.findPw.title') }}
      </template>
      <pw-find-form @success="pwChangeProcess" @failed="closeModal('findPwModal')"/>
      <template v-slot:footer>
      </template>
    </modal>
    <modal ref="changePwModal" :closable="true">
      <template v-slot:header>
        {{ $t('loginForm.modal.changePw.title') }}
      </template>
      <pw-change-form @success="pwChangeSuccess" @failed="closeModal('changePwModal')"/>
      <template v-slot:footer>
      </template>
    </modal>
    <modal ref="idenModal" :closable="true">
      <template v-slot:header>
        {{ $t('idenForm.title') }}
      </template>
      <iden-form @success="idenSuccess" @failed="idenFailed"/>
      <template v-slot:footer>
      </template>
    </modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import * as AuthApi from '@/api/auth';
import { usePromiseManager } from '@/utils/promiseManager';
import ErrCodes from '@/utils/loginErrCode';
import Modal from '@/components/Modal.vue'
import IdenForm from '@/components/IDEN/index.vue';
import PwChangeForm from '@/components/LOGIN/PwChangeForm.vue';
import IdFindForm from '@/components/LOGIN/IdFindForm.vue';
import PwFindForm from '@/components/LOGIN/PwFindForm.vue';

export default {
  name: 'LoginForm',
  components: {
    Modal,
    IdenForm,
    PwChangeForm,
    IdFindForm,
    PwFindForm,
  },
  data: () => ({
    password: '',
    isCapsLock: false,
    useRemem: false,
    userId: '',
  }),
  methods: {
    setFocus(refId){
      const comp = this.$refs[refId];
      if(comp !== undefined) comp.focus();
    },
    userCheckProcess(){
      this.popWait();
      return this.getLoginInfo({
        userId: this.userId,
        password: this.password,
      })
      .finally(() => {
        this.stopWait();
      })
      .catch((e) => {
        localStorage.rememId = '';
        let errCode = '-1'
        let errMsg = ''
        if(e.status === 200){
          errCode = e.data.LOGIN_ERR_CODE;
          errMsg = e.data.LOGIN_ERR_MSG;
          return this.loginErrProcess(errCode, errMsg);
        }
        return Promise.reject(errCode);
      })
    },
    ssoLoginProcess(){
      this.popWait();
      const returnUrl = this.urlQuery('rtn')||'';
      return AuthApi.ssoLoginUrl(returnUrl)
      .then((e) => {
        if(e.status === 200){
          const url = e.data;
          this.$router.push({name: 'empty'});
          return Promise.resolve(url);
        }
        return Promise.reject('-1');
      })
      .then((e) => {
        location.href = e;
      })
      .catch(() => {
        this.popAlert('loginForm.message.defaultErr');
        return Promise.reject();
      })
      .finally(() => {
        AuthApi.clearUserInfo();
        this.stopWait();
      })
    },
    loginProcess(){
      const canLogin = this.checkValid();
      if(!canLogin) return;

      this.userCheckProcess().then(() => {
        localStorage.useRemem = this.useRemem;
        localStorage.rememId = this.useRemem ? this.userId : '';
        return Promise.resolve();
      }).then(() => {
        return this.ssoLoginProcess();
      })
    },
    loginErrProcess(errCode, errMsg){
      let result = null;
      switch (errCode) {
        case ErrCodes.ID_NOT_FOUND:
        case ErrCodes.PASSWORD_ERROR:
        case ErrCodes.BLOCKED:
        case ErrCodes.ABNORMAL_APPROACH:
        case ErrCodes.ERROR_CNT_OVER:
        case ErrCodes.LIMITED_IP:
        case ErrCodes.LIMITED_ID:
        case ErrCodes.EXPIRED_ID:
          this.popAlert({
            text: errMsg,
          });
          AuthApi.clearUserInfo();
          break;
        case ErrCodes.EXPIRED_PASSWORD:
          this.popAlert('loginForm.message.error.expiredPassword');
          this.pwChangeProcess();
          break;
        case ErrCodes.INITIAL_PASSWORD:
          this.popAlert('loginForm.message.error.initialPassword');
          this.pwChangeProcess();
          break;
        case ErrCodes.DISAGREE_PERSONAL_INFO:
          this.popAlert('loginForm.message.error.disagrddPersonalInfo');
          break;
        case ErrCodes.LOGIN_NEED_2ND_CERT:
          this.showModal('idenModal');
          result = this.issuPromise('idenModal');
          break;
        default:
          this.popAlert('loginForm.message.error.defaultErr');
          AuthApi.clearUserInfo();
      }
      if(result === null) result = Promise.reject();
      return result;
    },
    checkValid(){
      if(this.userId === null || this.userId === undefined || this.userId === ''){
        this.popAlert('loginForm.message.valid.idEmpty')
        .then(() => {
          this.$refs.inputId.focus();
        });
        return false;
      }
      if(this.password === null || this.password === undefined || this.password === ''){
        this.popAlert('loginForm.message.valid.pwEmpty')
        .then(() => {
          this.$refs.inputPw.focus();
        });
        return false;
      }
      return true;
    },
    pwChangeProcess(){
      this.closeModal('findPwModal');
      this.showModal('changePwModal');
    },
    pwChangeSuccess(){
      this.closeModal('changePwModal');
      AuthApi.clearUserInfo();
    },
    idenSuccess(){
      this.closeModal('idenModal');
      this.resolvePromise('idenModal');
    },
    idenFailed(){
      this.closeModal('idenModal');
      this.rejectPromise('idenModal');
    },
    checkCapsLock(evt){
      this.isCapsLock = evt.getModifierState('CapsLock');
    },
    showModal(modalId){
      const modal = this.$refs[modalId];
      modal.show();
    },
    closeModal(modalId){
      const modal = this.$refs[modalId];
      modal.close();
    },
    ...usePromiseManager(),
    ...mapActions([
      'getLoginInfo',
      'popAlert',
      'popWait',
      'stopWait',
    ]),
  },
  computed: {
    ...mapGetters([
      'loginState',
      'urlQuery',
    ])
  },
  created(){
    this.useRemem = localStorage.useRemem||false;
    this.userId = localStorage.rememId||'';
  }
};
</script>
<style lang="scss" scoped>
  .login {
    > h3 {
      font-size: 20px;
      font-weight: 500;
    }
    .idInput {
      margin-top: 20px;
      background-image: url('~@/assets/image/id.png');
    }

    .pwInput {
      margin-top: 10px;
      background-image: url('~@/assets/image/pw.png');
    }

    > input {
      width: 100%;
      height: 50px;
      padding-left: 55px;
      border: 1px solid #ccc;
      background-repeat: no-repeat;
      background-color: #f5f6f8;

      &::placeholder {
        color: #999;
        font-size: 12px;
      }
      &:focus {
        outline: 1px solid #798796;
        &::placeholder {
          color: transparent;
        }
      }
    }

    .findBox {
      width: 100%;
      height: 20ox;
      font-size: 12px;
      color: #555;
      font-weight: 400;
      margin-top: 10px;
      display: flex;

      > label {
        width: 40%; line-height: 20px; cursor: pointer;
        > input {
          margin-right: 6px;
        }
      }

      .divBtnFlex {
        width: 60%;
        display: flex;
        justify-content: flex-end;

        > div {
          width: 1px;
          height: 13px;
          margin: 5px 10px 0 10px;
          background-color: #ddd;
        }
        > button {
          border: 0px none;
          background: transparent;
          font-size: 12px;
          font-weight: 400;
          color: #555;
          cursor: pointer;
          transition: 0.3s;

          &:hover {
            color: #798796;
          }
          &:focus{
            outline: 0px none;
          }
        }
      }
    }

    .btnLogin {
      display: block;
      width: 100%;
      height: 50px;
      margin-top: 30px;
      background-color: #0059a9;
      color: #fff;
      font-size: 20px;
      text-align: center;
      line-height: 50px;
      cursor: pointer;
      transition: 0.3s;
      border: 0px none;
      &:hover {
        background-color: #004788;
      }
    }

    .btnDown{
      margin-top: 5px;
      padding: 5px;
      float: right;
      background: #fff;
      color: #555;
      border: 0px none;
      cursor: pointer;
      font-size: 14px;
      &:hover {
        background-color: #555;
        color: #fff;
      }
    }
  }

  .valid-message {
    color: #ff0000;
    text-align: left;
    font-size: 10pt;
    margin-top: 5px;
  }
</style>
